import { KlCanvas } from '../../canvas/kl-canvas';
import { Easel } from './easel';
import { BB } from '../../../bb/bb';
import { throwIfNull } from '../../../bb/base/base';

export type TEaselProjectUpdaterParams<T extends string> = {
    klCanvas: KlCanvas;
    easel: Easel<T>;
};

/**
 * Allows KlCanvas to be rendered by Easel.
 * Call update when KlCanvas changed (added layer, moved layer, removed layer, changed selection, redo/undo)
 */
export class EaselProjectUpdater<T extends string> {
    private readonly klCanvas: KlCanvas;
    private readonly easel: Easel<T>;
    private compositeCanvas: HTMLCanvasElement | undefined;

    // ----------------------------------- public -----------------------------------
    constructor(p: TEaselProjectUpdaterParams<T>) {
        this.klCanvas = p.klCanvas;
        this.easel = p.easel;
        this.update();
    }

    update(): void {
        const width = this.klCanvas.getWidth();
        const height = this.klCanvas.getHeight();
        const layers = this.klCanvas.getLayersFast();

        // free resources if no compositing being done
        if (layers.some((layer) => layer.compositeObj)) {
            if (!this.compositeCanvas) {
                this.compositeCanvas = BB.canvas(width, height);
            }
        } else {
            if (this.compositeCanvas) {
                BB.freeCanvas(this.compositeCanvas);
                this.compositeCanvas = undefined;
            }
        }
        const compositeCanvas = this.compositeCanvas;

        // Detect if any layer has animation frames
        const hasAnimated = layers.some((layer) => layer.frames && layer.frames.length > 1);
        this.easel.setHasAnimatedLayers(hasAnimated);

        this.easel.setProject({
            width,
            height,
            layers: layers.map((layer) => {
                const hasFrames = layer.frames && layer.frames.length > 1;

                // Use a function for animated layers or composited layers
                if (hasFrames || (layer.compositeObj && compositeCanvas)) {
                    return {
                        image: () => {
                            // Pick the correct frame canvas
                            const frameCanvas = hasFrames
                                ? layer.frames![this.easel.getAnimFrameIndex() % layer.frames!.length]
                                : layer.canvas;

                            if (layer.compositeObj && compositeCanvas) {
                                if (compositeCanvas.width != width || compositeCanvas.height != height) {
                                    compositeCanvas.width = width;
                                    compositeCanvas.height = height;
                                }
                                const ctx = compositeCanvas.getContext('2d')!;
                                ctx.clearRect(0, 0, width, height);
                                ctx.drawImage(frameCanvas, 0, 0);
                                layer.compositeObj?.draw(throwIfNull(compositeCanvas.getContext('2d')));
                                return compositeCanvas;
                            }
                            return frameCanvas;
                        },
                        isVisible: layer.isVisible,
                        opacity: layer.opacity,
                        mixModeStr: layer.mixModeStr,
                        hasClipping: false,
                    };
                }

                return {
                    image: layer.canvas,
                    isVisible: layer.isVisible,
                    opacity: layer.opacity,
                    mixModeStr: layer.mixModeStr,
                    hasClipping: false,
                };
            }),
            selection: this.klCanvas.getSelection(),
        });
    }

    // if you're not rendering easel for a while
    freeCompositeCanvas(): void {
        if (this.compositeCanvas) {
            BB.freeCanvas(this.compositeCanvas);
            this.compositeCanvas = undefined;
        }
    }
}
